import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

const SITE_URL = 'https://www.krcomic.com';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || 'action');
  const [activeSlug, setActiveSlug] = useState(slug || 'action');
  const [sortBy, setSortBy] = useState<'popular' | 'latest'>('popular');

  const category = getCategoryBySlug(activeSlug);
  const comics = useMemo(() => {
    const list = getComicsByCategory(activeSlug);
    if (sortBy === 'popular') {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [activeSlug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <p className="text-foreground-500">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name}韩国漫画 - 免费在线观看 - 韩漫大全`}
        description={`韩漫大全${category.name}专区，为您提供最新最全的${category.name}类韩国漫画免费在线阅读。${category.description}，高清画质，每日更新。`}
        keywords={`${category.name},韩国漫画,免费漫画,在线观看,韩漫大全`}
        canonicalUrl={`${SITE_URL}/category/${activeSlug}`}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {category.name} 韩国漫画
            </h1>
            <p className="text-sm text-foreground-500">{category.description}</p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
              {categories.map(cat => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-foreground-500">排序：</span>
              <button
                onClick={() => setSortBy('popular')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === 'popular' ? 'bg-secondary-200 text-secondary-800' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最受欢迎
              </button>
              <button
                onClick={() => setSortBy('latest')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === 'latest' ? 'bg-secondary-200 text-secondary-800' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最新更新
              </button>
            </div>
          </div>

          {comics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-foreground-300 text-3xl" />
              </div>
              <p className="text-foreground-500">该分类暂无漫画作品</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {comics.map(comic => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                  data-product-shop
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-200">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      title={`${comic.title} - ${category.name}韩国漫画`}
                    />
                    <div className="absolute top-2 right-2">
                      {comic.status === '连载中' ? (
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded-md font-medium">
                          连载
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-secondary-500 text-background-50 text-xs rounded-md font-medium">
                          完结
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 truncate">{comic.author}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-xs" />
                    </div>
                    <span className="text-xs text-foreground-500">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">
                      · 更新至 {comic.chapters.length} 话
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}